/*
 * @Description: AI 智能看图（标签版）任务同步接口 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-03-03 09:23:00
 */
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { syncTask as syncTaskAction } from '../../../actions/aiTask'

export const AI_PHOTO_TAGS_TASK_NAME = 'group_photo_preview' as const

export type AIPhotoTagsTaskRoute = 'get_tags' | 'search' | 'photo_detail'

export interface SyncTaskParams<
  TInput extends Record<string, any> | string = Record<string, any> | string
> {
  userID?: string
  inputParam: TInput
  taskName: string
  taskRoute: AIPhotoTagsTaskRoute | string
}

export interface SyncTaskResponseData {
  status: number
  msg: string
  taskID?: string
  taskName?: string
  taskRoute?: string
  task_id?: string
  task_name?: string
  task_route?: string
  outputParam?: string
  output_param?: string
}

export interface SyncTaskResponse<TOutput = any> {
  code?: number
  msg?: string
  toast_msg?: string
  data?: SyncTaskResponseData
  output?: TOutput | null
}

export interface SyncTaskOptions {
  timeoutMs?: number
}

export interface GetTagsInput {
  group_id: string
}

export interface TagValue {
  value: string
}

export interface TagGroup {
  type: string
  values: TagValue[]
}

export interface GetTagsOutput {
  success: boolean
  message: string
  data: {
    tags: TagGroup[]
  }
}

export interface SearchInput {
  group_id: string
  page: number
  page_size: number
  keyword?: string
  selected_tags?: string[]
  tag_logic?: 'AND' | 'OR'
  date_range?: [string, string]
  media_type?: number
}

export interface PhotoSearchItem {
  group_id: string
  user_id: string
  photo_seq: number
  photo_content: string
  photo_file: string
  photo_time: string
  media_type: number
  thumbnail_url: string
  full_url: string
}

export interface SearchOutput {
  success: boolean
  message: string
  data: {
    photos: PhotoSearchItem[]
    total: number
    page: number
    page_size: number
  }
}

export interface PhotoDetailInput {
  group_id: string
  photo_seq: number
}

export interface PhotoDetailTag {
  tag_type: string
  tag_value: string
  reason: string
  confidence?: number | null
  suggestion?: string
  reference?: string
}

export interface PhotoDetailOutput {
  success: boolean
  message: string
  data: {
    group_id: string
    user_id: string
    photo_seq: number
    photo_content: string
    photo_file: string
    photo_time: string
    media_type: number
    deleted_type: number
    full_url: string
    tags: PhotoDetailTag[]
  }
}

const DEFAULT_TIMEOUT_MS = 60 * 1000

const parseOutputParam = <T,>(outputParam?: any): T | null => {
  if (!outputParam) return null
  if (typeof outputParam === 'object') return (outputParam as unknown) as T
  try {
    return JSON.parse(outputParam) as T
  } catch (error) {
    console.warn('解析 outputParam 失败:', error)
    return null
  }
}

const withTimeout = async <T,>(
  promise: Promise<T>,
  timeoutMs: number
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | null = null
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error('请求超时'))
    }, timeoutMs)
  })
  try {
    return (await Promise.race([promise, timeoutPromise])) as T
  } finally {
    if (timer) clearTimeout(timer)
  }
}

const useAITaskEvent = () => {
  const dispatch = useDispatch<any>()

  const syncTask = useCallback(
    async <
      TOutput = any,
      TInput extends Record<string, any> | string = Record<string, any> | string
    >(
      params: SyncTaskParams<TInput>,
      options?: SyncTaskOptions
    ): Promise<SyncTaskResponse<TOutput>> => {
      const payload: Record<string, any> = {
        taskName: params.taskName,
        taskRoute: params.taskRoute,
        inputParam: JSON.stringify(params.inputParam)
      }
      if (params.userID) payload.user_id = params.userID
      const timeoutMs =
        typeof options?.timeoutMs === 'number'
          ? options.timeoutMs
          : DEFAULT_TIMEOUT_MS
      try {
        const result = (await withTimeout(
          dispatch(syncTaskAction(payload)) as Promise<SyncTaskResponse>,
          timeoutMs
        )) as SyncTaskResponse
        const outputParam =
          result.data?.outputParam || result.data?.output_param
        return {
          ...result,
          output: parseOutputParam<TOutput>(outputParam)
        }
      } catch (error) {
        console.error('Sync task error:', error)
        throw error
      }
    },
    [dispatch]
  )

  const syncGetTags = useCallback(
    (input: GetTagsInput, options?: SyncTaskOptions) =>
      syncTask<GetTagsOutput, GetTagsInput>(
        {
          taskName: AI_PHOTO_TAGS_TASK_NAME,
          taskRoute: 'get_tags',
          inputParam: input
        },
        options
      ),
    [syncTask]
  )

  const syncSearch = useCallback(
    (input: SearchInput, options?: SyncTaskOptions) =>
      syncTask<SearchOutput, SearchInput>(
        {
          taskName: AI_PHOTO_TAGS_TASK_NAME,
          taskRoute: 'search',
          inputParam: input
        },
        options
      ),
    [syncTask]
  )

  const syncPhotoDetail = useCallback(
    (input: PhotoDetailInput, options?: SyncTaskOptions) =>
      syncTask<PhotoDetailOutput, PhotoDetailInput>(
        {
          taskName: AI_PHOTO_TAGS_TASK_NAME,
          taskRoute: 'photo_detail',
          inputParam: input
        },
        options
      ),
    [syncTask]
  )

  return {
    syncTask,
    syncGetTags,
    syncSearch,
    syncPhotoDetail
  }
}

export default useAITaskEvent
